import { computed, observable } from 'mobx';

import { makePaginated, Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import { AppClient, AppClientLicence, Licence } from '../../adl-gen/totalinteraction/futurefit/db';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { AppClientLicenceListEntry } from '../ui/components/lists/appclient-licence-list/appclient-licence-list-view';

import { DataStore, TransportLayer } from './datastore';

class AppClientLicenceApi implements TransportLayer<AppClientLicence> {
  kind: string = 'AppClientLicence';
  constructor(protected readonly service: Service) {}
  /// TODO: Not client admin-safe - but that's ok
  create = async (item: AppClientLicence): Promise<DbKey<AppClientLicence>> =>
    this.service.createAppClientLicence(item);
  update = async (itemWithId: WithDbId<AppClientLicence>): Promise<void> =>
    this.service.updateAppClientLicence(itemWithId);
  doDelete = async (id: DbKey<AppClientLicence>): Promise<void> => this.service.deleteAppClientLicence(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<AppClientLicence>>> =>
    this.service.queryAppClientLicences(q);

  queryLicences = async (q: TableQuery): Promise<Paginated<WithDbId<Licence>>> => this.service.queryLicences(q);
}

export class AppClientLicenceStore extends DataStore<AppClientLicence> {
  @observable clientId?: DbKey<AppClient>;
  @observable clientLicence?: Promise<AppClientLicenceListEntry>;
  /// No need to be an observable
  licenceTypes: Promise<Paginated<WithDbId<Licence>>>;
  @observable list: Promise<Paginated<AppClientLicenceListEntry>>;

  public static fromService = (
    service: Service,
    identity: LoggedIn,
    appClientId?: DbKey<AppClient>,
    id?: DbKey<AppClientLicence>
  ): AppClientLicenceStore => {
    const store = new AppClientLicenceStore(new AppClientLicenceApi(service), identity, appClientId, id);
    return store;
  };

  public constructor(
    transport: TransportLayer<AppClientLicence>,
    identity: LoggedIn | LoggedInSuper,
    appClientId?: DbKey<AppClient>,
    id?: DbKey<AppClientLicence>
  ) {
    super(transport, identity, id);
    this.clientId = appClientId;
    // TODO: do we need this?
    // Don't autoload all data, we might only want a subset (eg. setting a client ID) - hence not in base class
    // this.data = this.init(id)
    if (id) {
      this.clientLicence = this.selectLicence(id);
    }
    this.licenceTypes = this.initData();
    this.list = this.initList();
  }

  private readonly initList = () => {
    return this.loadList(this.clientId);
    // Don't call reload() here - or indeed anything else
  };

  // /** @override to filter on appClientId */
  // init = async (id?: DbKey<AppClientLicence>): Promise<Paginated<WithDbId<AppClientLicence>>> => {
  //   if (this.isSuperUser) {
  //     if (id) {
  //       return this.load(id)
  //     } else {
  //       const results = await (this.clientId
  //         ? this.query(this.queryId(this.clientId, "appClientId"))
  //         : this.query(this.queryAll))
  //       return results
  //     }
  //   } else { return Promise.reject("Not authorized") }
  // }

  private readonly initData = () => {
    return this.loadData();
  };

  private readonly loadList = async (appClientId?: DbKey<AppClient>): Promise<Paginated<AppClientLicenceListEntry>> => {
    if (this.isSuperUser) {
      // if (this.authorizedForClient(appClientId)) {
      const results = await (appClientId
        ? this.queryList(this.queryId(appClientId, 'appClientId'))
        : this.queryList(this.queryAll));
      return results;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadData = async (): Promise<Paginated<WithDbId<Licence>>> => {
    if (this.isSuperUser) {
      // if (this.clientId && this.authorizedForClient(this.clientId)) {
      const data = await this.queryData();
      return data;
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly queryList = async (q: TableQuery): Promise<Paginated<AppClientLicenceListEntry>> => {
    const results = await (this.transport as AppClientLicenceApi).query(q);
    return makePaginated({
      items: results.items.map(item => ({
        id: item.id,
        ...item.value,
        identity: this.isSuperUser ? 'super' : 'client-admin'
      })),
      current_offset: 0,
      total_size: results.items.length
    });
  };

  private readonly queryData = async (): Promise<Paginated<WithDbId<Licence>>> => {
    const result = await (this.transport as AppClientLicenceApi).queryLicences(this.queryAll);
    return result;
  };

  // load = async (id: DbKey<AppClientLicence>): Promise<Paginated<WithDbId<AppClientLicence>>> => {
  //   const licence = await (this.transport as AppClientLicenceApi).query(this.queryId(id))
  //   return licence;
  // }

  @computed
  public get loadingList(): MaybeLoading<Paginated<AppClientLicenceListEntry>> {
    if (this.isSuperUser) {
      // if (this.authorized) {
      return fromPromise(this.list);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  @computed
  public get loadingData(): MaybeLoading<Paginated<WithDbId<Licence>>> {
    if (this.isSuperUser) {
      // if (this.authorized) {
      return fromPromise(this.licenceTypes);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  /// Replaces base getter 'selected'
  @computed
  public get selectedLicence(): MaybeLoading<AppClientLicenceListEntry> | undefined {
    if (this.isSuperUser) {
      // if (this.authorized) {
      if (this.clientLicence) {
        return fromPromise(this.clientLicence);
      } else {
        return undefined;
      }
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  /// Adds to base method 'setSelected'
  private readonly selectLicence = async (id: DbKey<AppClientLicence>): Promise<AppClientLicenceListEntry> => {
    if (this.authorized) {
      const results = await this.queryList(this.queryId(id));
      // TODO: dereferencing an item that may not exist in the array?
      return results.items.filter(e => e.id === id)[0];
    } else {
      return Promise.reject('Not authorized');
    }
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/app-client-licence-store.ts